import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Youtube, Search, CheckCircle2, AlertTriangle, ExternalLink, Loader2, Settings, RefreshCw, Film, Sparkles, Link2, BarChart3 } from 'lucide-react';
import { neonQuery, youtubeProxy, callClaude } from '../services/api';
import { getCurrentUser } from '../services/auth';

// AI editor — browser-direct Claude, same model + brand-voice rules as the Meta
// caption assistant (OrganicBuilder). Key ships in the bundle, like LandingPages.
const AI_MODEL = 'claude-sonnet-4-6';

/**
 * YouTube Upload — pick a Media Library video, set title/description/tags, and
 * publish it straight to the CrabPlace YouTube channel.
 *
 * Uploading needs a one-time Google OAuth consent (a service account can't post
 * to a channel), handled by the Connect card. Heavy lifting (R2 -> YouTube) runs
 * in youtube-upload-background; this page just queues + polls.
 */

const CATEGORIES = [
  { id: '22', label: 'People & Blogs' },
  { id: '26', label: 'Howto & Style' },
  { id: '24', label: 'Entertainment' },
  { id: '27', label: 'Education' },
  { id: '19', label: 'Travel & Events' },
  { id: '23', label: 'Comedy' },
  { id: '15', label: 'Pets & Animals' },
  { id: '28', label: 'Science & Technology' },
  { id: '1', label: 'Film & Animation' },
];

const fmtDur = (s) => {
  const n = Math.round(Number(s) || 0);
  if (!n) return '';
  const m = Math.floor(n / 60);
  return `${m}:${String(n % 60).padStart(2, '0')}`;
};

const money = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[$,]/g, '')) || 0;
  return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const STATUS_STYLE = {
  queued: { bg: '#eef1f4', fg: '#6e6e73', label: 'Queued' },
  uploading: { bg: '#fff4e6', fg: '#b3550f', label: 'Uploading…' },
  published: { bg: '#e6f6ec', fg: '#1b7a3d', label: 'Published' },
  failed: { bg: '#fdecec', fg: '#c0392b', label: 'Failed' },
};

export default function YouTubeUploader() {
  const user = getCurrentUser();

  const [conn, setConn] = useState(null);          // null=loading, else status object
  const [connBusy, setConnBusy] = useState(false);

  // setup card
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [setupSaving, setSetupSaving] = useState(false);

  // video picker
  const [videos, setVideos] = useState([]);
  const [vidLoading, setVidLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  // compose
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [category, setCategory] = useState('26');
  const [privacy, setPrivacy] = useState('public');
  const [destinationUrl, setDestinationUrl] = useState('https://www.crabplace.com/');
  const [campaign, setCampaign] = useState('');
  const [watermark, setWatermark] = useState(true);
  const logoInputRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);            // { type, text }

  // AI editor
  const [aiIdea, setAiIdea] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiErr, setAiErr] = useState(null);
  const [titleOptions, setTitleOptions] = useState(null);

  const [uploads, setUploads] = useState([]);
  const pollRef = useRef(null);

  // YouTube-attributed orders (from the crabplace.co links in descriptions)
  const [ytStats, setYtStats] = useState(null);
  const [ytOrders, setYtOrders] = useState([]);

  // ── status ──────────────────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      const s = await youtubeProxy('status');
      setConn(s);
      if (s.categoryId && !CATEGORIES.find((c) => c.id === category)) setCategory(s.categoryId);
    } catch (e) {
      setConn({ connected: false, error: e.message });
    }
  }, [category]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Re-check when the user returns from the Google consent tab.
  useEffect(() => {
    const onFocus = () => loadStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadStatus]);

  // ── videos ──────────────────────────────────────────────────────────────
  const loadVideos = useCallback(async () => {
    setVidLoading(true);
    try {
      const rows = await neonQuery('media_assets_list', {
        search, mediaKind: 'video', minQuality: 0,
        usableForAny: [], tagsAll: [], excludeTags: [],
        starredOnly: false, includeArchived: false, orderBy: 'newest',
        limit: 60, offset: 0,
      });
      setVideos(rows || []);
    } catch (e) {
      setMsg({ type: 'error', text: 'Could not load videos: ' + e.message });
    } finally { setVidLoading(false); }
  }, [search]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  // ── uploads list + polling ────────────────────────────────────────────────
  const loadUploads = useCallback(async () => {
    try {
      const r = await youtubeProxy('list');
      setUploads(r.uploads || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadUploads(); }, [loadUploads]);

  const loadAttribution = useCallback(async () => {
    try {
      const sum = await neonQuery('attribution_summary', { channel: 'youtube' });
      setYtStats(sum && sum[0] ? sum[0] : null);
      const list = await neonQuery('attribution_list', { channel: 'youtube', limit: 8, offset: 0 });
      setYtOrders(list || []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { loadAttribution(); }, [loadAttribution]);

  useEffect(() => {
    const active = uploads.some((u) => u.status === 'queued' || u.status === 'uploading');
    if (active && !pollRef.current) {
      pollRef.current = setInterval(loadUploads, 4000);
    } else if (!active && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current && !active) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [uploads, loadUploads]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── actions ────────────────────────────────────────────────────────────────
  const pickVideo = (v) => {
    setSelected(v);
    if (!title.trim()) setTitle(v.suggested_name || v.original_name || '');
    if (!description.trim() && v.alt_text) setDescription(v.alt_text);
    setTitleOptions(null);
    // Already-watermarked assets (from the Media Library export tool) shouldn't be
    // double-stamped — default the watermark off for them.
    setWatermark(!(Array.isArray(v.tags) && v.tags.includes('watermarked')));
    setMsg(null);
  };

  // AI editor — writes the title (3 options), description (with a clickable
  // crabplace.com link, which YouTube descriptions allow), and tags, seeded from
  // the picked video's Media Library description + an optional angle.
  async function generateMeta() {
    if (aiLoading) return;
    if (!selected && !aiIdea.trim()) { setAiErr('Pick a video or type an angle first.'); return; }
    setAiLoading(true); setAiErr(null);
    const v = selected || {};
    const ctx = selected
      ? `The video (length ${fmtDur(v.duration_s) || 'unknown'}) is described in our media library as: "${v.alt_text || v.suggested_name || v.original_name || ''}"${(v.tags && v.tags.length) ? ` (tags: ${v.tags.join(', ')})` : ''}.`
      : '';
    const idea = aiIdea.trim() ? `The angle to emphasize: ${aiIdea.trim()}.` : '';
    const sys = `You write YouTube metadata for The Crab Place (crabplace.com), a Maryland seafood retailer that ships live and steamed blue crabs and other seafood across the country. Write a title, description, and tags for a video that will be PUBLIC on their YouTube channel. Voice: warm, Maryland-proud, helpful, never salesy. Brand voice rules (this copy is customer-facing): NO em dashes or en dashes ever (use periods, commas, or parentheses); write number ranges as "1 to 2"; avoid the "not just X, it's Y" crutch, rule-of-three stacking, and buzzwords (elevate, indulge, crafted, curated, nestled). A few relevant emojis in the description are fine.
- title: under 100 characters, specific and search-friendly, include natural keywords a seafood shopper would type (Maryland crabs, blue crab, crab boil, etc.) when they fit. No ALL CAPS, no clickbait.
- description: 2 to 4 short paragraphs. In the first or second line, include the exact token {{SHOP_LINK}} once (we replace it with a tracked crabplace.co link). Do NOT write any crabplace.com or crabplace.co URL yourself. Say what the video shows and add a warm call to action to order at that link. You may add 3 to 6 relevant hashtags on the last line.
- tags: 10 to 15 short lowercase search phrases.
Return ONLY a JSON object: {"titles":["...","...","..."],"description":"...","tags":["...","..."]} with exactly 3 distinct title options. No other text.`;
    try {
      const data = await callClaude({ model: AI_MODEL, max_tokens: 1200, system: sys, messages: [{ role: 'user', content: `Write YouTube title, description, and tags. ${ctx} ${idea}`.trim() }] });
      const text = (data.content || []).filter((b) => b.type === 'text').map((b) => b.text).join('');
      let obj = null;
      try { obj = JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1)); } catch { obj = null; }
      if (!obj) throw new Error('Could not read the AI response. Try again.');
      const titles = (obj.titles || []).filter(Boolean).map((t) => String(t).replace(/[<>]/g, '').slice(0, 100));
      setTitleOptions(titles.length ? titles : null);
      if (titles[0]) setTitle(titles[0]);
      if (obj.description) setDescription(String(obj.description).replace(/[<>]/g, ''));
      if (Array.isArray(obj.tags)) setTags(obj.tags.filter(Boolean).join(', '));
    } catch (e) { setAiErr(e.message); }
    finally { setAiLoading(false); }
  }

  const saveSetup = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { setMsg({ type: 'error', text: 'Enter both the Client ID and Client Secret.' }); return; }
    setSetupSaving(true);
    try {
      await youtubeProxy('set-config', { clientId: clientId.trim(), clientSecret: clientSecret.trim() });
      setClientSecret('');
      await loadStatus();
      setMsg({ type: 'ok', text: 'Saved. Now click Connect YouTube.' });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSetupSaving(false); }
  };

  const connect = async () => {
    setConnBusy(true);
    try {
      const r = await youtubeProxy('auth-url');
      if (r.needsSetup || !r.url) { setMsg({ type: 'error', text: 'Add the app credentials below first.' }); setConnBusy(false); return; }
      window.open(r.url, '_blank', 'noopener');
      // Poll for the consent to land (the callback stores the token in the other tab).
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        const s = await youtubeProxy('status').catch(() => null);
        if (s && s.connected) { clearInterval(iv); setConn(s); setConnBusy(false); setMsg({ type: 'ok', text: 'Connected to ' + (s.channelTitle || 'your channel') + '.' }); }
        else if (tries > 40) { clearInterval(iv); setConnBusy(false); }
      }, 3000);
    } catch (e) { setMsg({ type: 'error', text: e.message }); setConnBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect CPx from YouTube? You will need to reconnect to upload again.')) return;
    await youtubeProxy('disconnect');
    loadStatus();
  };

  // Downscale the chosen logo to a PNG data URL (keeps transparency) before
  // storing it — the overlay only needs a few hundred px.
  function downscalePng(file, maxW) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        const ctx = c.getContext('2d'); ctx.clearRect(0, 0, w, h); ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('could not read image')); };
      img.src = url;
    });
  }
  async function onLogoFile(e) {
    const file = e.target.files && e.target.files[0];
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (!file) return;
    setMsg(null);
    try {
      const dataUrl = await downscalePng(file, 600);
      await youtubeProxy('set-watermark-logo', { logoBase64: dataUrl });
      setMsg({ type: 'ok', text: 'Watermark logo saved.' });
      loadStatus();
    } catch (err) { setMsg({ type: 'error', text: 'Could not save the logo: ' + err.message }); }
  }

  const send = async () => {
    if (!selected) { setMsg({ type: 'error', text: 'Pick a video first.' }); return; }
    if (!title.trim()) { setMsg({ type: 'error', text: 'A title is required.' }); return; }
    setSending(true); setMsg(null);
    try {
      const r = await youtubeProxy('start-upload', {
        assetId: selected.id,
        title: title.trim(),
        description,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        categoryId: category,
        privacy,
        watermark,
        destinationUrl: destinationUrl.trim() || 'https://www.crabplace.com/',
        campaign: campaign.trim() || null,
        createdBy: user?.staffName || user?.id || null,
      });
      if (r.error) { setMsg({ type: 'error', text: r.error }); }
      else {
        setMsg({ type: 'ok', text: 'Sent to YouTube — it will appear below as it uploads.' });
        setSelected(null); setTitle(''); setDescription(''); setTags('');
        loadUploads();
      }
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setSending(false); }
  };

  const connected = conn && conn.connected;
  const needsSetup = conn && conn.needsSetup;

  return (
    <div style={{ padding: '0 4px' }}>
      <div className="breadcrumb" style={{ marginBottom: 12 }}>
        <span>Marketing</span><span> / </span><a>YouTube Upload</a>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Youtube size={26} color="#FF0000" />
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--navy)' }}>Send to YouTube</h1>
      </div>

      {msg ? (
        <div style={{
          margin: '0 0 14px', padding: '10px 14px', borderRadius: 8, fontSize: 13,
          background: msg.type === 'ok' ? '#e6f6ec' : '#fdecec',
          color: msg.type === 'ok' ? '#1b7a3d' : '#c0392b',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {msg.type === 'ok' ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}{msg.text}
        </div>
      ) : null}

      {/* ── Connection ─────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>YouTube account</span>
          <button className="btn btn-ghost" onClick={loadStatus} title="Re-check" style={{ padding: '4px 8px' }}><RefreshCw size={14} /></button>
        </div>
        <div className="card-body">
          {conn === null ? (
            <div style={{ color: '#6e6e73', display: 'flex', gap: 8, alignItems: 'center' }}><Loader2 size={15} className="spin" /> Checking…</div>
          ) : connected ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <CheckCircle2 size={18} color="#1b7a3d" />
              <span style={{ fontSize: 14 }}>Connected{conn.channelTitle ? <> as <strong>{conn.channelTitle}</strong></> : ''}.</span>
              <button className="btn btn-outline" onClick={disconnect} style={{ marginLeft: 'auto' }}>Disconnect</button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: '#6e6e73', marginBottom: 12 }}>
                {needsSetup
                  ? 'One-time setup: paste your Google OAuth app credentials, then connect the channel.'
                  : 'Connect the CrabPlace YouTube channel so CPx can upload videos to it.'}
              </div>
              {!needsSetup ? (
                <button className="btn btn-primary" onClick={connect} disabled={connBusy}>
                  {connBusy ? <><Loader2 size={14} className="spin" /> Waiting for Google…</> : 'Connect YouTube'}
                </button>
              ) : null}

              {/* Setup credentials + instructions */}
              <details open={needsSetup} style={{ marginTop: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--navy)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Settings size={14} /> Google app setup
                </summary>
                <div style={{ marginTop: 12, fontSize: 12.5, color: '#444', lineHeight: 1.6 }}>
                  <ol style={{ paddingLeft: 18, margin: '0 0 12px' }}>
                    <li>In <a href="https://console.cloud.google.com/apis/library/youtube.googleapis.com" target="_blank" rel="noreferrer">Google Cloud Console</a>, enable the <strong>YouTube Data API v3</strong>.</li>
                    <li>Configure the OAuth consent screen and <strong>publish it</strong> (so the connection doesn't expire). Add scopes for YouTube upload + read.</li>
                    <li>Create an <strong>OAuth client ID</strong> of type <strong>Web application</strong>. Under "Authorized redirect URIs," add exactly:</li>
                  </ol>
                  <code style={{ display: 'block', background: '#f5f5f7', border: '1px solid #e5e5ea', borderRadius: 6, padding: '8px 10px', fontSize: 12, wordBreak: 'break-all', marginBottom: 12 }}>
                    {(conn && conn.redirectUri) || 'https://cpx-prototype.netlify.app/.netlify/functions/youtube-proxy'}
                  </code>
                  <div className="form-group" style={{ marginBottom: 8 }}>
                    <label className="form-label">Client ID</label>
                    <input className="form-input" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label">Client Secret</label>
                    <input className="form-input" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-…" />
                  </div>
                  <button className="btn btn-navy" onClick={saveSetup} disabled={setupSaving}>{setupSaving ? 'Saving…' : 'Save credentials'}</button>
                </div>
              </details>
              {conn && conn.authError ? (
                <div style={{ marginTop: 10, fontSize: 12, color: '#c0392b' }}>Connection check failed: {conn.authError}. Try Connect again.</div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* ── Compose ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        {/* video picker */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span>1. Pick a video</span>
            <div style={{ position: 'relative', flex: '0 0 220px' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9b9b9f' }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search videos…"
                className="form-input" style={{ paddingLeft: 28, height: 30, fontSize: 12 }} />
            </div>
          </div>
          <div className="card-body" style={{ maxHeight: 460, overflowY: 'auto' }}>
            {vidLoading && videos.length === 0 ? (
              <div style={{ color: '#6e6e73', padding: 8 }}>Loading videos…</div>
            ) : videos.length === 0 ? (
              <div style={{ color: '#6e6e73', padding: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Film size={16} /> No videos in the Media Library yet. Upload one there first.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                {videos.map((v) => {
                  const isSel = selected && selected.id === v.id;
                  return (
                    <button key={v.id} onClick={() => pickVideo(v)} style={{
                      textAlign: 'left', padding: 0, cursor: 'pointer', borderRadius: 7, overflow: 'hidden',
                      background: '#fff', border: isSel ? '2px solid var(--orange)' : '1px solid #e5e5ea',
                    }}>
                      <div style={{ position: 'relative', aspectRatio: '16 / 9', background: '#0e0e10' }}>
                        {v.r2_thumb_key ? (
                          <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(v.r2_thumb_key)}`} alt=""
                            loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.92 }} />
                        ) : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Film size={20} color="#fff" /></div>}
                        {v.duration_s ? (
                          <span style={{ position: 'absolute', right: 5, bottom: 5, background: 'rgba(0,0,0,0.78)', color: '#fff', fontSize: 10.5, padding: '1px 5px', borderRadius: 3, fontVariantNumeric: 'tabular-nums' }}>{fmtDur(v.duration_s)}</span>
                        ) : null}
                        {isSel ? <span style={{ position: 'absolute', top: 5, left: 5, background: 'var(--orange)', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CheckCircle2 size={13} /></span> : null}
                      </div>
                      <div style={{ padding: '6px 8px', fontSize: 11.5, color: 'var(--navy)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.suggested_name || v.original_name || `Video ${v.id}`}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* details */}
        <div className="card">
          <div className="card-header">2. Title, description &amp; publish</div>
          <div className="card-body">
            {selected ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, padding: 8, background: '#f5f5f7', borderRadius: 8 }}>
                {selected.r2_thumb_key ? <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(selected.r2_thumb_key)}`} alt="" style={{ width: 72, height: 41, objectFit: 'cover', borderRadius: 4 }} /> : <Film size={20} />}
                <div style={{ fontSize: 12.5, color: 'var(--navy)' }}>
                  <div style={{ fontWeight: 600 }}>{selected.suggested_name || selected.original_name}</div>
                  <div style={{ color: '#6e6e73' }}>{fmtDur(selected.duration_s)}{selected.duration_s ? ' · ' : ''}selected video</div>
                </div>
              </div>
            ) : <div style={{ fontSize: 13, color: '#6e6e73', marginBottom: 12 }}>Pick a video on the left to begin.</div>}

            {/* AI editor */}
            <div style={{ border: '1px dashed #d9c7ff', background: '#faf7ff', borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                <Sparkles size={15} color="#7c3aed" />
                <span style={{ fontWeight: 600, fontSize: 13, color: '#5b21b6' }}>Write it for me</span>
              </div>
              <input className="form-input" value={aiIdea} onChange={(e) => setAiIdea(e.target.value)}
                placeholder="Optional: the angle to emphasize. Leave blank to use the video's description."
                style={{ marginBottom: 8, fontSize: 12.5 }} />
              <button onClick={generateMeta} disabled={aiLoading || (!selected && !aiIdea.trim())}
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: (aiLoading || (!selected && !aiIdea.trim())) ? 'not-allowed' : 'pointer', opacity: (aiLoading || (!selected && !aiIdea.trim())) ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                {aiLoading ? <><Loader2 size={14} className="spin" /> Writing…</> : <><Sparkles size={14} /> Write title, description &amp; tags</>}
              </button>
              {aiErr ? <div style={{ fontSize: 12, color: '#c0392b', marginTop: 8 }}>{aiErr}</div> : null}
              {titleOptions && titleOptions.length > 1 ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11.5, color: '#6e6e73', marginBottom: 5 }}>Title options (click to use):</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {titleOptions.map((t, i) => (
                      <button key={i} onClick={() => setTitle(t)} title="Use this title"
                        style={{ textAlign: 'left', fontSize: 12.5, padding: '6px 9px', borderRadius: 6, cursor: 'pointer',
                          border: title === t ? '1.5px solid #7c3aed' : '1px solid #e5d9ff', background: title === t ? '#f1e9ff' : '#fff', color: 'var(--navy)' }}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Title <span style={{ color: '#9b9b9f', fontWeight: 400 }}>({title.length}/100)</span></label>
              <input className="form-input" value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} placeholder="Video title shown on YouTube" />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Description</label>
              <textarea className="form-textarea" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this video is about, links, etc." />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Tags <span style={{ color: '#9b9b9f', fontWeight: 400 }}>(comma separated)</span></label>
              <input className="form-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="maryland crabs, seafood, crab boil" />
            </div>

            {/* Tracking link */}
            <div style={{ background: '#f3f9f4', border: '1px solid #d8ead8', borderRadius: 8, padding: 11, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#1b7a3d', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Link2 size={13} /> Tracking link in the description
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
                  <label className="form-label">Link goes to</label>
                  <input className="form-input" value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} placeholder="https://www.crabplace.com/" />
                </div>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Campaign <span style={{ color: '#9b9b9f', fontWeight: 400 }}>(optional)</span></label>
                  <input className="form-input" value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="auto from title" />
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: '#6e6e73', marginTop: 7, lineHeight: 1.5 }}>
                On publish, a branded <strong>crabplace.co</strong> link (UTM-tagged for YouTube) replaces <code>{'{{SHOP_LINK}}'}</code> in the description, or is added to it. Sales from this video then show up under <em>Orders attributed to YouTube</em> below.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Category</label>
                <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Visibility</label>
                <select className="form-select" value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            {/* Watermark */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, fontSize: 12.5 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', color: 'var(--navy)' }}>
                <input type="checkbox" checked={watermark} onChange={(e) => setWatermark(e.target.checked)} />
                Add logo watermark <span style={{ color: '#9b9b9f' }}>(bottom-right)</span>
              </label>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {conn && conn.watermarkLogoSet
                  ? <span style={{ color: '#1b7a3d' }}>logo set ✓</span>
                  : <span style={{ color: '#b3550f' }}>no logo</span>}
                <button onClick={() => logoInputRef.current && logoInputRef.current.click()} className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }}>
                  {conn && conn.watermarkLogoSet ? 'Replace' : 'Upload logo'}
                </button>
                <input ref={logoInputRef} type="file" accept="image/png,image/*" onChange={onLogoFile} style={{ display: 'none' }} />
              </span>
            </div>
            {watermark && conn && !conn.watermarkLogoSet ? (
              <div style={{ fontSize: 12, color: '#b3550f', marginBottom: 10, marginTop: -4 }}>Upload a transparent PNG logo to turn on the watermark (or uncheck it).</div>
            ) : null}

            <button className="btn btn-primary" onClick={send} disabled={sending || !connected || !selected || !title.trim()} style={{ width: '100%' }}>
              {sending ? <><Loader2 size={14} className="spin" /> Sending…</> : <><Youtube size={15} /> Send to YouTube</>}
            </button>
            {!connected ? <div style={{ fontSize: 12, color: '#b3550f', marginTop: 8, textAlign: 'center' }}>Connect a YouTube account above first.</div> : null}
          </div>
        </div>
      </div>

      {/* ── Recent uploads ─────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Recent uploads</span>
          <button className="btn btn-ghost" onClick={loadUploads} style={{ padding: '4px 8px' }}><RefreshCw size={14} /></button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {uploads.length === 0 ? (
            <div style={{ padding: 16, color: '#6e6e73', fontSize: 13 }}>Nothing uploaded yet.</div>
          ) : (
            <table className="orders-table" style={{ width: '100%' }}>
              <thead>
                <tr><th>Title</th><th>Visibility</th><th>Status</th><th>By</th><th>When</th><th></th></tr>
              </thead>
              <tbody>
                {uploads.map((u) => {
                  const st = STATUS_STYLE[u.status] || STATUS_STYLE.queued;
                  return (
                    <tr key={u.id}>
                      <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.title}</td>
                      <td style={{ textTransform: 'capitalize' }}>{u.privacy}</td>
                      <td>
                        <span style={{ background: st.bg, color: st.fg, padding: '2px 9px', borderRadius: 11, fontSize: 11.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          {(u.status === 'queued' || u.status === 'uploading') ? <Loader2 size={11} className="spin" /> : null}{st.label}
                        </span>
                        {u.status === 'failed' && u.error ? <div style={{ fontSize: 11, color: '#c0392b', marginTop: 3, maxWidth: 320 }}>{u.error}</div> : null}
                      </td>
                      <td style={{ fontSize: 12, color: '#6e6e73' }}>{u.created_by || '—'}</td>
                      <td style={{ fontSize: 12, color: '#6e6e73', whiteSpace: 'nowrap' }}>{u.created_at ? new Date(u.created_at).toLocaleString() : ''}</td>
                      <td>
                        {u.youtube_url ? <a href={u.youtube_url} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}>View <ExternalLink size={12} /></a> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Orders attributed to YouTube ─────────────────────────── */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><BarChart3 size={15} /> Orders attributed to YouTube</span>
          <button className="btn btn-ghost" onClick={loadAttribution} style={{ padding: '4px 8px' }}><RefreshCw size={14} /></button>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 28, marginBottom: ytOrders.length ? 14 : 0, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy)' }}>{ytStats ? Number(ytStats.total_attributed || 0) : 0}</div>
              <div style={{ fontSize: 12, color: '#6e6e73' }}>orders from YouTube links</div>
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700, color: '#1b7a3d' }}>{money(ytStats ? ytStats.total_revenue : 0)}</div>
              <div style={{ fontSize: 12, color: '#6e6e73' }}>revenue</div>
            </div>
          </div>
          {ytOrders.length === 0 ? (
            <div style={{ fontSize: 12.5, color: '#6e6e73' }}>No YouTube-attributed orders yet. Once a shopper clicks a crabplace.co link from one of your video descriptions and orders, it shows up here.</div>
          ) : (
            <table className="orders-table" style={{ width: '100%' }}>
              <thead><tr><th>Order</th><th>Customer</th><th>Amount</th><th>Campaign</th><th>When</th></tr></thead>
              <tbody>
                {ytOrders.map((o, i) => (
                  <tr key={o.id || o.bc_order_id || i}>
                    <td>{o.bc_order_id || '—'}</td>
                    <td style={{ fontSize: 12 }}>{[o.ofirstname, o.olastname].filter(Boolean).join(' ') || '—'}</td>
                    <td>{money(o.orderamount)}</td>
                    <td style={{ fontSize: 12, color: '#6e6e73' }}>{o.utm_campaign || '—'}</td>
                    <td style={{ fontSize: 12, color: '#6e6e73', whiteSpace: 'nowrap' }}>{o.created_at ? new Date(o.created_at).toLocaleDateString() : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
